import {
    Button,
    Box,
    Center,
    Flex,
    HStack,
    VStack,
    Text,
    Heading,
    Alert,
    AlertIcon,
    AlertDescription,
    Icon,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Tooltip,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    useToast,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    PopoverArrow,
} from '@chakra-ui/react';
import { TbFlag, TbCheck, TbCircleMinus, TbMessage, TbAlertTriangle } from 'react-icons/tb';
import React, { useMemo, useState } from 'react';
import PartType from '../PartType';
import { formatPrice } from '../../functions';
import PartOptionsModal from '../../modals/PartOptionsModal';
import { RekeySection } from '../../hooks/useGetOrderDetail';
import { useManuallyAttemptRekey } from '../../hooks/useManuallyAttemptRekey';
import { useSkipRekey } from '../../hooks/useSkipRekey';
import PartBayButton from '../PartBayButton';
import CollapsibleSearch from '../CollapsibleSearch';

interface RekeyTabsProps {
    rekeyData: RekeySection;
    orderId: string;
    drpJob?: boolean;
}

const RekeyTabs = ({ rekeyData, orderId, drpJob = false }: RekeyTabsProps) => {
    const [selectedPartId, setSelectedPartId] = React.useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const manualRekeyMutation = useManuallyAttemptRekey();
    const skipRekeyMutation = useSkipRekey();
    const toast = useToast();

    const handleSkipRekey = () => {
        skipRekeyMutation.mutate({ orderId }, {
            onSuccess: () => {
                toast({
                    title: 'Rekeying for this file has been skipped',
                    status: 'success',
                    duration: 7000,
                    isClosable: true,
                });
            },
            onError: () => {
                toast({
                    title: 'Error',
                    description: 'Failed to skip rekey.',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            },
        });
    };

    const handleAttemptRekey = () => {
        manualRekeyMutation.mutate({ orderId }, {
            onSuccess: () => {
                setIsConfirmOpen(false);
                toast({
                    title: 'Rekey requested',
                    description: 'A rekey attempt has been queued. This may take a few minutes.',
                    status: 'success',
                    duration: 5000,
                    isClosable: true,
                });
            },
            onError: (error: any) => {
                const status = error?.response?.status;
                if (status === 409) {
                    toast({
                        title: 'Unable to attempt rekey',
                        description: 'A rekey has already been manually attempted within the last 24 hours.',
                        status: 'warning',
                        duration: 5000,
                        isClosable: true,
                    });
                } else {
                    toast({
                        title: 'Error',
                        description: 'Failed to request rekey.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                }
                setIsConfirmOpen(false);
            },
        });
    };

    const filteredRekeyParts = useMemo(() => {
        if (!searchQuery.trim()) return rekeyData.parts;
        const q = searchQuery.toLowerCase().trim();
        return rekeyData.parts.filter((part) =>
            part.title.toLowerCase().includes(q) ||
            (part.originalPartNumber && part.originalPartNumber.toLowerCase().includes(q)) ||
            (part.modifiedPartNumber && part.modifiedPartNumber.toLowerCase().includes(q)) ||
            (part.originalPartType && part.originalPartType.toLowerCase().includes(q)) ||
            (part.modifiedPartType && part.modifiedPartType.toLowerCase().includes(q))
        );
    }, [rekeyData.parts, searchQuery]);

    const numUnsuccessful = useMemo(() => {
        if (!rekeyData) return 0;
        const total = rekeyData.parts.reduce((tot: number, p: any) => (
            p.status === 'skipped' || p.status === 'flagged' ? tot + 1 : tot
        ), 0);
        return total > 0 ? total : 0;
    }, [rekeyData]);

    const hasPreviouslyRekeyedParts = useMemo(() => {
        if (!rekeyData) return false;
        return rekeyData.parts.some((p) => p.status !== 'not-started');
    }, [rekeyData]);

    const handleRowClick = (partId: string) => {
        setSelectedPartId(partId);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedPartId(null);
    };

    const confirmModal = (
        <AttemptRekeyConfirmModal
            isOpen={isConfirmOpen}
            onClose={() => setIsConfirmOpen(false)}
            onConfirm={handleAttemptRekey}
            isLoading={manualRekeyMutation.isPending}
        />
    );

    if (rekeyData.status === 'not-started' && !hasPreviouslyRekeyedParts) {
        return (
            <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflow="hidden">
                <Flex justify="space-between" align="center" px={6} pt={5} pb={4}>
                    <Heading size="md" color="gray.800">Estimate</Heading>
                    <HStack spacing={3} align="center">
                        <CollapsibleSearch
                            isCompact={false}
                            width="350px"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <Box flexShrink={0}>
                            <PartBayButton
                                colorScheme="partbay-blue"
                                variant="solid"
                                size="md"
                                onClick={() => setIsConfirmOpen(true)}
                                text="Attempt Re-Key"
                            />
                        </Box>
                        <Box flexShrink={0}>
                            <PartBayButton
                                colorScheme="partbay-blue"
                                variant="outline"
                                size="md"
                                onClick={handleSkipRekey}
                                isLoading={skipRekeyMutation.isPending}
                                loadingText="Skipping..."
                                text="Skip Rekey"
                            />
                        </Box>
                    </HStack>
                </Flex>
                <Box position="relative">
                    <Box
                        borderTop="1px solid"
                        borderColor="gray.200"
                        overflowX="auto"
                        opacity={0.4}
                    >
                        <Table variant="simple" size="md" w="100%" sx={{ 'th:first-of-type, td:first-of-type': { pl: 6 }, 'th:last-of-type, td:last-of-type': { pr: 6 } }}>
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th colSpan={4} borderBottomWidth="0px"></Th>
                                    <Th
                                        colSpan={3}
                                        textAlign="center"
                                        color="gray.600"
                                        fontSize="xs"
                                        fontWeight="bold"
                                        letterSpacing="wider"
                                        borderLeft="1px solid"
                                        borderColor="gray.200"
                                    >
                                        ESTIMATE
                                    </Th>
                                    <Th
                                        colSpan={3}
                                        textAlign="center"
                                        color="gray.600"
                                        fontSize="xs"
                                        fontWeight="bold"
                                        letterSpacing="wider"
                                        borderLeft="1px solid"
                                        borderColor="gray.200"
                                    >
                                        PART CHANGES
                                    </Th>
                                </Tr>
                                <Tr>
                                    <Th w="40px"></Th>
                                    <Th color="gray.600" fontSize="xs">LN</Th>
                                    <Th color="gray.600" fontSize="xs">PART DESCRIPTION</Th>
                                    <Th color="gray.600" fontSize="xs" textAlign="center">QTY</Th>
                                    <Th color="gray.600" fontSize="xs" borderLeft="1px solid" borderColor="gray.200">PART TYPE</Th>
                                    <Th color="gray.600" fontSize="xs">PART NUMBER</Th>
                                    <Th color="gray.600" fontSize="xs" isNumeric>LIST PRICE</Th>
                                    <Th color="gray.600" fontSize="xs" borderLeft="1px solid" borderColor="gray.200">PART TYPE</Th>
                                    <Th color="gray.600" fontSize="xs">PART NUMBER</Th>
                                    <Th color="gray.600" fontSize="xs" isNumeric>LIST PRICE</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {[1, 2, 3, 4, 5].map(i => (
                                    <Tr key={i}>
                                        <Td py={4} w="50px"><Box h="24px" w="24px" bg="gray.100" borderRadius="full" /></Td>
                                        <Td><Box h="14px" w="20px" bg="gray.100" borderRadius="sm" /></Td>
                                        <Td><Box h="14px" w="180px" bg="gray.100" borderRadius="sm" /></Td>
                                        <Td textAlign="center"><Box h="14px" w="20px" bg="gray.100" borderRadius="sm" mx="auto" /></Td>
                                        <Td borderLeft="1px solid" borderColor="gray.100"><Box h="14px" w="40px" bg="gray.100" borderRadius="sm" /></Td>
                                        <Td><Box h="14px" w="100px" bg="gray.100" borderRadius="sm" /></Td>
                                        <Td isNumeric><Box h="14px" w="60px" bg="gray.100" borderRadius="sm" ml="auto" /></Td>
                                        <Td borderLeft="1px solid" borderColor="gray.100"><Box h="14px" w="40px" bg="gray.100" borderRadius="sm" /></Td>
                                        <Td><Box h="14px" w="100px" bg="gray.100" borderRadius="sm" /></Td>
                                        <Td isNumeric><Box h="14px" w="60px" bg="gray.100" borderRadius="sm" ml="auto" /></Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </Box>
                    <Center
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        zIndex={1}
                    >
                        <Alert
                            status='info'
                            borderRadius="md"
                            border="1px solid"
                            borderColor='blue.400'
                            bg='blue.50'
                            maxW="500px"
                            boxShadow="none"
                        >
                            <AlertIcon color="blue.500" />
                            <AlertDescription color="gray.700" fontSize="sm">
                                Rekeying for this order has not started yet. Check back soon!
                            </AlertDescription>
                        </Alert>
                    </Center>
                </Box>
                {confirmModal}
            </Box>
        );
    }

    return (
        <Box>
            {rekeyData.status === 'not-started' && hasPreviouslyRekeyedParts &&
                <Alert
                    status="info"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="blue.400"
                    bg="blue.50"
                    mb={4}
                >
                    <AlertIcon color="blue.500" />
                    <AlertDescription color="gray.700" fontSize="sm">
                        Re-keying was completed for a previous order but there are more ordered parts that are waiting for confirmation and to then be re-keyed.
                    </AlertDescription>
                </Alert>
            }
            {rekeyData.status === 'skipped' &&
                <Alert
                    status="info"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="blue.400"
                    bg="blue.50"
                    mb={4}
                >
                    <AlertIcon color="blue.500" />
                    <AlertDescription color="gray.700" fontSize="sm">
                        Re-keying skipped because the file was already checked out, or the changes failed to save. We will try again shortly.
                    </AlertDescription>
                </Alert>
            }
            {numUnsuccessful > 0 && rekeyData.status !== 'failed' &&
                <Alert
                    status="warning"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="orange.400"
                    bg="orange.50"
                    mb={4}
                >
                    <AlertIcon color="orange.500" />
                    <AlertDescription color="gray.700" fontSize="sm">
                        {rekeyData.status === 'needed'
                            ? `${numUnsuccessful} part${numUnsuccessful === 1 ? '' : 's'} ${numUnsuccessful === 1 ? 'needs' : 'need'} changing in the estimate. Please manually adjust if necessary.`
                            : `${numUnsuccessful} part${numUnsuccessful === 1 ? '' : 's'} ${numUnsuccessful === 1 ? 'was' : 'were'} not changed successfully in the estimate. Please manually adjust if necessary.`}
                    </AlertDescription>
                </Alert>
            }
            {rekeyData.status === 'failed' &&
                <Alert
                    status="error"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="red.400"
                    bg="red.50"
                    mb={4}
                >
                    <AlertIcon color="red.500" />
                    <AlertDescription color="gray.700" fontSize="sm">
                        Rekeying for this order has failed.{rekeyData.rekeyError ? ` ${rekeyData.rekeyError}` : ''}
                    </AlertDescription>
                </Alert>
            }
            <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflow="hidden">
                <Flex justify="space-between" align="center" px={6} pt={5} pb={4}>
                    <Heading size="md" color="gray.800">Estimate</Heading>
                    <HStack spacing={3} align="center">
                        <CollapsibleSearch
                            isCompact={false}
                            width="350px"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {/* {rekeyData.status !== 'needed' && (
                            <Box flexShrink={0}>
                                <PartBayButton
                                    colorScheme="partbay-blue"
                                    variant="solid"
                                    size="md"
                                    onClick={() => setIsConfirmOpen(true)}
                                    text="Attempt Re-Key"
                                />
                            </Box>
                        )}
                        {(rekeyData.status === 'skipped' || rekeyData.status === 'not-started') && (
                            <Box flexShrink={0}>
                                <PartBayButton
                                    colorScheme="partbay-blue"
                                    variant="outline"
                                    size="md"
                                    onClick={handleSkipRekey}
                                    isLoading={skipRekeyMutation.isPending}
                                    loadingText="Skipping..."
                                    text="Skip Rekey"
                                />
                            </Box>
                        )} */}
                    </HStack>
                </Flex>

                <Box
                    borderTop="1px solid"
                    borderColor="gray.200"
                    overflowX="auto"
                >
                    <Table variant="simple" size="md" w="100%" sx={{ 'th:first-of-type, td:first-of-type': { pl: 6 }, 'th:last-of-type, td:last-of-type': { pr: 6 } }}>
                        <Thead bg="gray.50">
                            <Tr>
                                <Th colSpan={3} borderBottomWidth="0px"></Th>
                                <Th
                                    colSpan={4}
                                    textAlign="center"
                                    color="gray.600"
                                    fontSize="xs"
                                    fontWeight="bold"
                                    letterSpacing="wider"
                                    borderLeft="1px solid"
                                    borderColor="gray.200"
                                >
                                    ESTIMATE
                                </Th>
                                <Th
                                    colSpan={4}
                                    textAlign="center"
                                    color="gray.600"
                                    fontSize="xs"
                                    fontWeight="bold"
                                    letterSpacing="wider"
                                    borderLeft="1px solid"
                                    borderColor="gray.200"
                                >
                                    PART CHANGES
                                </Th>
                            </Tr>
                            <Tr>
                                <Th w="40px"></Th>
                                <Th color="gray.600" fontSize="xs">LN</Th>
                                <Th color="gray.600" fontSize="xs">PART DESCRIPTION</Th>

                                {/* Estimate Columns */}
                                <Th color="gray.600" fontSize="xs" textAlign="center" borderLeft="1px solid" borderColor="gray.200">QTY</Th>
                                <Th color="gray.600" fontSize="xs">PART TYPE</Th>
                                <Th color="gray.600" fontSize="xs">PART NUMBER</Th>
                                <Th color="gray.600" fontSize="xs" isNumeric>LIST PRICE</Th>

                                {/* Part Changes Columns */}
                                <Th color="gray.600" fontSize="xs" textAlign="center" borderLeft="1px solid" borderColor="gray.200">QTY</Th>
                                <Th color="gray.600" fontSize="xs">PART TYPE</Th>
                                <Th color="gray.600" fontSize="xs">PART NUMBER</Th>
                                <Th color="gray.600" fontSize="xs" isNumeric>LIST PRICE</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {filteredRekeyParts.map(part => (
                                <Tr
                                    key={part._id}
                                    _hover={{
                                        cursor: 'pointer',
                                        bg: part.advisor ? 'yellow.100' : part.status === 'skipped' ? 'gray.100' : part.status === 'flagged' ? 'orange.100' : part.status === 'completed' ? 'green.100' : 'gray.50'
                                    }}
                                    bg={part.advisor ? 'yellow.50' : part.status === 'skipped' ? 'gray.50' : part.status === 'flagged' ? 'orange.50' : part.status === 'completed' ? 'green.50' : 'transparent'}
                                    onClick={() => handleRowClick(part._id)}
                                >
                                    {/* Status / Advisor Icon */}
                                    <Td py={4} whiteSpace="nowrap">
                                        <Box display="inline-flex" alignItems="center" gap="6px" verticalAlign="middle">
                                            {part.status === 'flagged' && (
                                                <Popover placement="right" trigger="hover" isLazy openDelay={150}>
                                                    <PopoverTrigger>
                                                        <Center
                                                            w="24px"
                                                            h="24px"
                                                            bg="orange.100"
                                                            borderRadius="full"
                                                            flexShrink={0}
                                                            cursor="pointer"
                                                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                        >
                                                            <Icon as={TbFlag as React.ElementType} color="orange.600" w={4} h={4} />
                                                        </Center>
                                                    </PopoverTrigger>
                                                    <PopoverContent
                                                        bg="orange.100"
                                                        borderColor="orange.400"
                                                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                        w="360px"
                                                        whiteSpace="normal"
                                                    >
                                                        <PopoverArrow bg="orange.100" />
                                                        <PopoverBody px={6} py={4}>
                                                            <VStack align="stretch" spacing={2}>
                                                                <HStack spacing={3}>
                                                                    <Icon as={TbFlag as React.ElementType} w={5} h={5} color="orange.700" />
                                                                    <Text fontWeight="semibold" fontSize="sm" color="orange.900">
                                                                        Flagged
                                                                    </Text>
                                                                </HStack>
                                                                <Text fontSize="sm" color="gray.800">
                                                                    {part.flagReason || 'No reason provided.'}
                                                                </Text>
                                                            </VStack>
                                                        </PopoverBody>
                                                    </PopoverContent>
                                                </Popover>
                                            )}
                                            {!part.advisor && part.status === 'completed' && (
                                                <Tooltip label="Success" placement="top" hasArrow>
                                                    <Center w="24px" h="24px" bg="green.100" borderRadius="full" flexShrink={0}>
                                                        <Icon as={TbCheck as React.ElementType} color="green.600" w={4} h={4} />
                                                    </Center>
                                                </Tooltip>
                                            )}
                                            {!part.advisor && part.status === 'skipped' && (
                                                <Tooltip label="Skipped" placement="top" hasArrow>
                                                    <Center w="24px" h="24px" bg="gray.100" borderRadius="full" flexShrink={0}>
                                                        <Icon as={TbCircleMinus as React.ElementType} color="gray.600" w={4} h={4} />
                                                    </Center>
                                                </Tooltip>
                                            )}
                                            {part.advisor && (
                                                <Popover placement="right" trigger="hover" isLazy openDelay={150}>
                                                    <PopoverTrigger>
                                                        <Center
                                                            as="button"
                                                            type="button"
                                                            h="24px"
                                                            px={2}
                                                            gap={1}
                                                            border="1px solid"
                                                            borderColor="orange.300"
                                                            borderRadius="md"
                                                            cursor="pointer"
                                                            flexShrink={0}
                                                            _hover={{ bg: 'orange.100' }}
                                                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                        >
                                                            <Icon as={TbAlertTriangle as React.ElementType} w={3.5} h={3.5} color="orange.500" />
                                                            <Text fontWeight="bold" fontSize="xs" color="orange.600" lineHeight="1">
                                                                {part.advisor.score}
                                                            </Text>
                                                        </Center>
                                                    </PopoverTrigger>
                                                    <PopoverContent
                                                        bg="orange.50"
                                                        borderColor="orange.200"
                                                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                        w="400px"
                                                        whiteSpace="normal"
                                                    >
                                                        <PopoverArrow bg="orange.50" />
                                                        <PopoverBody px={6} py={4}>
                                                            <VStack align="stretch" spacing={3}>
                                                                <HStack spacing={3}>
                                                                    <Icon as={TbAlertTriangle as React.ElementType} w={5} h={5} color="orange.500" />
                                                                    <Text fontWeight="semibold" fontSize="sm" color="orange.800">
                                                                        Advisor Score: {part.advisor.score}
                                                                    </Text>
                                                                </HStack>
                                                                <Text fontSize="sm" color="gray.700">
                                                                    {part.advisor.description}
                                                                </Text>
                                                                <HStack spacing={8}>
                                                                    <VStack align="start" spacing={0}>
                                                                        <Text fontSize="xs" color="gray.500" fontWeight="medium">ACTUAL PRICE</Text>
                                                                        <Text fontSize="sm" fontWeight="medium" color="gray.900">
                                                                            {part.advisor.actualPrice !== null ? formatPrice(part.advisor.actualPrice) : '—'}
                                                                        </Text>
                                                                    </VStack>
                                                                    <VStack align="start" spacing={0}>
                                                                        <Text fontSize="xs" color="gray.500" fontWeight="medium">ALLOWABLE PRICE</Text>
                                                                        <Text fontSize="sm" fontWeight="medium" color="gray.900">
                                                                            {part.advisor.allowablePrice !== null ? formatPrice(part.advisor.allowablePrice) : '—'}
                                                                        </Text>
                                                                    </VStack>
                                                                    <VStack align="start" spacing={0}>
                                                                        <Text fontSize="xs" color="gray.500" fontWeight="medium">VARIANCE</Text>
                                                                        <Text fontSize="sm" fontWeight="medium" color={part.advisor.variance !== null && part.advisor.variance < 0 ? 'red.600' : 'gray.900'}>
                                                                            {part.advisor.variance !== null ? formatPrice(part.advisor.variance) : '—'}
                                                                        </Text>
                                                                    </VStack>
                                                                </HStack>
                                                            </VStack>
                                                        </PopoverBody>
                                                    </PopoverContent>
                                                </Popover>
                                            )}
                                        </Box>
                                    </Td>

                                    {/* Line Number */}
                                    <Td fontWeight="medium" color="gray.700">
                                        {part.lineNumber ?? '—'}
                                    </Td>

                                    {/* Description with Note Icon */}
                                    <Td>
                                        <HStack spacing={1} justify="space-between">
                                            <Text fontWeight="medium" color="gray.900">
                                                {part.title}
                                            </Text>
                                            {part.lineNotes && (
                                                <Tooltip label={part.lineNotes} placement="top" hasArrow maxW="300px">
                                                    <Box as="span" display="inline-flex" flexShrink={0}>
                                                        <Icon as={TbMessage as React.ElementType} w={4} h={4} color="blue.500" />
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        </HStack>
                                    </Td>

                                    {/* Estimate Section */}
                                    <Td textAlign="center" borderLeft="1px solid" borderColor="gray.100">
                                        {part.originalQty ?? part.qty}
                                    </Td>
                                    <Td>
                                        <PartType partType={part.originalPartType} />
                                    </Td>
                                    <Td fontSize="sm" fontFamily="mono" color="gray.700">
                                        {part.originalPartNumber}
                                    </Td>
                                    <Td isNumeric fontWeight="medium" color="gray.900">
                                        {formatPrice(part.originalPrice)}
                                    </Td>

                                    {/* Part Changes Section */}
                                    <Td textAlign="center" borderLeft="1px solid" borderColor="gray.100">
                                        {part.modifiedQty != null ? (
                                            part.modifiedQty
                                        ) : (
                                            <Text color="gray.400" fontSize="sm">---</Text>
                                        )}
                                    </Td>
                                    <Td>
                                        {part.modifiedPartType ? (
                                            <PartType partType={part.modifiedPartType} />
                                        ) : (
                                            <Text color="gray.400" fontSize="sm">---</Text>
                                        )}
                                    </Td>
                                    <Td fontSize="sm" fontFamily="mono" color="gray.700">
                                        {part.modifiedPartNumber || (
                                            <Text color="gray.400" as="span">---</Text>
                                        )}
                                    </Td>
                                    <Td isNumeric fontWeight="medium" color="gray.900">
                                        {part.modifiedPrice === 0 ? <Text color="gray.400" as="span">Incl.</Text> : part.modifiedPrice ? formatPrice(part.modifiedPrice) : (
                                            <Text color="gray.400" as="span">---</Text>
                                        )}
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </Box>
            </Box>

            {rekeyData && selectedPartId && (
                <PartOptionsModal
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    orderPartId={selectedPartId}
                    orderId={orderId || ''}
                    source="order"
                    drpJob={drpJob}
                />
            )}
            {confirmModal}
        </Box>
    );
};

export default RekeyTabs;

interface AttemptRekeyConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isLoading: boolean;
}
const AttemptRekeyConfirmModal = ({ isOpen, onClose, onConfirm, isLoading }: AttemptRekeyConfirmModalProps) => (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
            <ModalHeader>Attempt Rekey</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
                <VStack spacing={3} align="stretch">
                    <Text color="gray.700">
                        Are you sure you want to manually attempt a rekey for this order?
                    </Text>
                    <Alert status="warning" borderRadius="md" variant="left-accent">
                        <AlertIcon />
                        <Text fontSize="sm" color="gray.600">
                            You can only manually attempt a rekey once every 24 hours.
                        </Text>
                    </Alert>
                </VStack>
            </ModalBody>
            <ModalFooter>
                <HStack spacing={3}>
                    <Button
                        size="action"
                        variant="outlineGray"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <PartBayButton
                        size="action"
                        colorScheme="partbay-blue"
                        variant="solid"
                        onClick={onConfirm}
                        isLoading={isLoading}
                        loadingText="Requesting..."
                        text="Confirm"
                    />
                </HStack>
            </ModalFooter>
        </ModalContent>
    </Modal>
);

